/**
 * TM Service — Firestore CRUD for tmUsers, tasks, and task history.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  addDoc, deleteDoc, query, where, orderBy, serverTimestamp,
  arrayUnion, arrayRemove,
  Timestamp, onSnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  TMUser, TMUserRole, TMUserStatus, TMUserPermissions,
  Task, TaskStatus, TaskPriority, TMUserRef,
  TaskHistoryEntry, TaskHistoryAction, TaskHistoryChange,
  TMProject, ProjectStatus,
} from '@/types/tm.types';
import { DEFAULT_TM_PERMISSIONS } from '@/types/tm.types';

// ─── Converters ───────────────────────────────────────────────────────────────

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

function docToTMUser(data: Record<string, unknown>, uid: string): TMUser {
  return {
    uid,
    name:        (data.name        as string) ?? '',
    email:       (data.email       as string) ?? '',
    phone:       (data.phone       as string) ?? '',
    status:      (data.status      as TMUserStatus) ?? 'pending',
    role:        (data.role        as TMUserRole)   ?? 'member',
    permissions: (data.permissions as TMUserPermissions) ?? DEFAULT_TM_PERMISSIONS,
    avatarUrl:   (data.avatarUrl   as string | null) ?? null,
    createdAt:   toDate(data.createdAt),
    approvedAt:  toDate(data.approvedAt),
    approvedBy:  (data.approvedBy  as string | null) ?? null,
  };
}

function docToTask(data: Record<string, unknown>, id: string): Task {
  return {
    id,
    projectId:    (data.projectId   as string | null) ?? null,
    title:        (data.title       as string) ?? '',
    description:  (data.description as string | null) ?? null,
    assignedTo:   (data.assignedTo  as TMUserRef | null) ?? null,
    assignedBy:   (data.assignedBy  as TMUserRef),
    status:       (data.status      as TaskStatus)   ?? 'todo',
    priority:     (data.priority    as TaskPriority) ?? 'medium',
    dueDate:      toDate(data.dueDate),
    reminderDays: (data.reminderDays as number) ?? 1,
    progress:     (data.progress    as number) ?? 0,
    tags:         (data.tags        as string[]) ?? [],
    createdAt:    toDate(data.createdAt),
    updatedAt:    toDate(data.updatedAt),
  };
}

function docToProject(data: Record<string, unknown>, id: string): TMProject {
  return {
    id,
    name:        (data.name        as string) ?? '',
    description: (data.description as string | null) ?? null,
    color:       (data.color       as string) ?? 'indigo',
    status:      (data.status      as ProjectStatus) ?? 'active',
    memberUids:  (data.memberUids  as string[]) ?? [],
    createdBy:   (data.createdBy   as TMUserRef),
    createdAt:   toDate(data.createdAt),
    updatedAt:   toDate(data.updatedAt),
  };
}

function docToHistory(data: Record<string, unknown>, id: string): TaskHistoryEntry {
  return {
    id,
    userId:    (data.userId   as string) ?? '',
    userName:  (data.userName as string) ?? '',
    action:    (data.action   as TaskHistoryAction) ?? 'edited',
    comment:   (data.comment  as string | null) ?? null,
    changes:   (data.changes  as TaskHistoryChange[]) ?? [],
    createdAt: toDate(data.createdAt),
  };
}

// ─── TM Users ─────────────────────────────────────────────────────────────────

export async function getTMUser(uid: string): Promise<TMUser | null> {
  const snap = await getDoc(doc(db, 'tmUsers', uid));
  if (!snap.exists()) return null;
  return docToTMUser(snap.data() as Record<string, unknown>, uid);
}

export async function registerTMUser(
  uid: string, name: string, email: string, phone: string,
): Promise<void> {
  await setDoc(doc(db, 'tmUsers', uid), {
    name, email, phone,
    status:      'pending',
    role:        'member',
    permissions: DEFAULT_TM_PERMISSIONS,
    avatarUrl:   null,
    createdAt:   serverTimestamp(),
    approvedAt:  null,
    approvedBy:  null,
  });
}

export async function getAllTMUsers(): Promise<TMUser[]> {
  const snap = await getDocs(
    query(collection(db, 'tmUsers'), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => docToTMUser(d.data() as Record<string, unknown>, d.id));
}

export async function getApprovedTMUsers(): Promise<TMUser[]> {
  // Avoid compound index requirement by filtering status only, sort client-side
  const snap = await getDocs(
    query(collection(db, 'tmUsers'), where('status', '==', 'approved'))
  );
  const users = snap.docs.map(d => docToTMUser(d.data() as Record<string, unknown>, d.id));
  return users.sort((a, b) => a.name.localeCompare(b.name));
}

export async function approveTMUser(
  uid: string, approverUid: string, role: TMUserRole,
): Promise<void> {
  await updateDoc(doc(db, 'tmUsers', uid), {
    status:    'approved',
    role,
    approvedAt: serverTimestamp(),
    approvedBy: approverUid,
  });
}

export async function rejectTMUser(uid: string): Promise<void> {
  await updateDoc(doc(db, 'tmUsers', uid), { status: 'rejected' });
}

export async function deactivateTMUser(uid: string): Promise<void> {
  await updateDoc(doc(db, 'tmUsers', uid), { status: 'deactivated' });
}

export async function updateTMUserRole(uid: string, role: TMUserRole): Promise<void> {
  await updateDoc(doc(db, 'tmUsers', uid), { role });
}

export async function updateTMUserPermissions(
  uid: string, permissions: TMUserPermissions,
): Promise<void> {
  await updateDoc(doc(db, 'tmUsers', uid), { permissions });
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

export async function getAllTasks(): Promise<Task[]> {
  const snap = await getDocs(
    query(collection(db, 'tasks'), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => docToTask(d.data() as Record<string, unknown>, d.id));
}

export async function getMyTasks(uid: string): Promise<Task[]> {
  const snap = await getDocs(
    query(
      collection(db, 'tasks'),
      where('assignedTo.uid', '==', uid),
      orderBy('createdAt', 'desc'),
    )
  );
  return snap.docs.map(d => docToTask(d.data() as Record<string, unknown>, d.id));
}

export async function getTask(id: string): Promise<Task | null> {
  const snap = await getDoc(doc(db, 'tasks', id));
  if (!snap.exists()) return null;
  return docToTask(snap.data() as Record<string, unknown>, id);
}

export function subscribeTask(id: string, cb: (t: Task | null) => void): Unsubscribe {
  return onSnapshot(doc(db, 'tasks', id), snap => {
    cb(snap.exists() ? docToTask(snap.data() as Record<string, unknown>, snap.id) : null);
  });
}

export interface CreateTaskInput {
  projectId:    string | null;
  title:        string;
  description:  string | null;
  assignedTo:   TMUserRef | null;
  assignedBy:   TMUserRef;
  priority:     TaskPriority;
  dueDate:      Date | null;
  reminderDays: number;
  tags:         string[];
}

export async function createTask(input: CreateTaskInput): Promise<string> {
  const ref = await addDoc(collection(db, 'tasks'), {
    ...input,
    projectId: input.projectId ?? null,
    dueDate:   input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
    status:    'todo' as TaskStatus,
    progress:  0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getProjectTasks(projectId: string): Promise<Task[]> {
  const snap = await getDocs(
    query(collection(db, 'tasks'), where('projectId', '==', projectId), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => docToTask(d.data() as Record<string, unknown>, d.id));
}

export async function updateTask(
  id: string,
  fields: Partial<Omit<Task, 'id' | 'createdAt'>>,
): Promise<void> {
  const data: Record<string, unknown> = { ...fields, updatedAt: serverTimestamp() };
  if (fields.dueDate instanceof Date) data.dueDate = Timestamp.fromDate(fields.dueDate);
  await updateDoc(doc(db, 'tasks', id), data);
}

export async function deleteTask(id: string): Promise<void> {
  await deleteDoc(doc(db, 'tasks', id));
}

// ─── Task History ─────────────────────────────────────────────────────────────

export async function addTaskHistory(
  taskId: string,
  entry: {
    userId:   string;
    userName: string;
    action:   TaskHistoryAction;
    comment?: string;
    changes?: TaskHistoryChange[];
  },
): Promise<void> {
  await addDoc(collection(db, 'tasks', taskId, 'history'), {
    userId:    entry.userId,
    userName:  entry.userName,
    action:    entry.action,
    comment:   entry.comment ?? null,
    changes:   entry.changes ?? [],
    createdAt: serverTimestamp(),
  });
}

export async function getTaskHistory(taskId: string): Promise<TaskHistoryEntry[]> {
  const snap = await getDocs(
    query(
      collection(db, 'tasks', taskId, 'history'),
      orderBy('createdAt', 'desc'),
    )
  );
  return snap.docs.map(d => docToHistory(d.data() as Record<string, unknown>, d.id));
}

export function subscribeTaskHistory(
  taskId: string,
  cb: (entries: TaskHistoryEntry[]) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'tasks', taskId, 'history'), orderBy('createdAt', 'desc')),
    snap => cb(snap.docs.map(d => docToHistory(d.data() as Record<string, unknown>, d.id))),
  );
}

// ─── WhatsApp helpers ─────────────────────────────────────────────────────────

export function buildWhatsAppUrl(phone: string, message: string): string {
  const clean = phone.replace(/[^\d+]/g, '');
  return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
}

export function taskAssignedWhatsAppMessage(
  memberName: string, taskTitle: string, dueDate: Date | null, appUrl: string,
): string {
  const due = dueDate
    ? dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'No due date';
  return `Hi ${memberName.split(' ')[0]},\n\nYou have been assigned a new task on Angels Army Team Manager:\n\n*${taskTitle}*\nDue: ${due}\n\nLog in to view details: ${appUrl}`;
}

export function taskUpdatedWhatsAppMessage(
  memberName: string, taskTitle: string, updateSummary: string, appUrl: string,
): string {
  return `Hi ${memberName.split(' ')[0]},\n\nThere is an update on your task:\n\n*${taskTitle}*\n${updateSummary}\n\nLog in to view details: ${appUrl}`;
}

export function taskReminderWhatsAppMessage(
  memberName: string, taskTitle: string, daysLeft: number, appUrl: string,
): string {
  const when = daysLeft === 0 ? 'today' : daysLeft === 1 ? 'tomorrow' : `in ${daysLeft} days`;
  return `Hi ${memberName.split(' ')[0]},\n\nReminder: your task is due *${when}*:\n\n*${taskTitle}*\n\nLog in to update progress: ${appUrl}`;
}

export function userApprovedWhatsAppMessage(memberName: string, appUrl: string): string {
  return `Hi ${memberName.split(' ')[0]},\n\nGreat news! Your Angels Army Team Manager account has been *approved*. You can now log in and start collaborating:\n\n${appUrl}`;
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export interface CreateProjectInput {
  name:        string;
  description: string | null;
  color:       string;
  memberUids:  string[];
  createdBy:   TMUserRef;
}

export async function createProject(input: CreateProjectInput): Promise<string> {
  const ref = await addDoc(collection(db, 'tmProjects'), {
    ...input,
    status:    'active' as ProjectStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getProject(id: string): Promise<TMProject | null> {
  const snap = await getDoc(doc(db, 'tmProjects', id));
  if (!snap.exists()) return null;
  return docToProject(snap.data() as Record<string, unknown>, snap.id);
}

export async function getAllProjects(): Promise<TMProject[]> {
  const snap = await getDocs(
    query(collection(db, 'tmProjects'), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => docToProject(d.data() as Record<string, unknown>, d.id));
}

export async function getMyProjects(uid: string): Promise<TMProject[]> {
  const snap = await getDocs(
    query(collection(db, 'tmProjects'), where('memberUids', 'array-contains', uid))
  );
  const projects = snap.docs.map(d => docToProject(d.data() as Record<string, unknown>, d.id));
  return projects.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
}

export async function updateProject(
  id: string,
  fields: Partial<Pick<TMProject, 'name' | 'description' | 'color' | 'status'>>,
): Promise<void> {
  await updateDoc(doc(db, 'tmProjects', id), { ...fields, updatedAt: serverTimestamp() });
}

export async function deleteProject(id: string): Promise<void> {
  await deleteDoc(doc(db, 'tmProjects', id));
}

export async function addProjectMember(projectId: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'tmProjects', projectId), {
    memberUids: arrayUnion(uid),
    updatedAt:  serverTimestamp(),
  });
}

export async function removeProjectMember(projectId: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'tmProjects', projectId), {
    memberUids: arrayRemove(uid),
    updatedAt:  serverTimestamp(),
  });
}
